import { loadData, peekData } from "@/lib/toolkit/data-cache";
import type { Certainty, WhatIfScenario } from "./types";

const KEY = "whatif-scenarios";

/**
 * Loads the whole scenario dataset exactly once per session (same convention
 * as the JEE Toolkit's static JSON). Search, filtering, Surprise Me and stage
 * navigation all run against this in-memory copy — zero database traffic.
 */
export function loadScenarios(): Promise<WhatIfScenario[]> {
  return loadData<WhatIfScenario[]>(
    KEY,
    () => import("./data/scenarios.json") as unknown as Promise<{ default: WhatIfScenario[] }>,
  );
}

export function peekScenarios(): WhatIfScenario[] | undefined {
  return peekData<WhatIfScenario[]>(KEY);
}

export const SCENARIOS_KEY = KEY;

/** Case-insensitive client-side match over title, description and tags. */
export function matchScenario(s: WhatIfScenario, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    s.title.toLowerCase().includes(q) ||
    s.shortDescription.toLowerCase().includes(q) ||
    s.tags.some((t) => t.toLowerCase().includes(q))
  );
}

export function pickRandom(list: WhatIfScenario[], exceptSlug?: string): WhatIfScenario | null {
  const pool = exceptSlug ? list.filter((s) => s.slug !== exceptSlug) : list;
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}
/** Distinct certainty labels used by a scenario's stages — powers the certainty filter. */
export function scenarioCertainties(s: WhatIfScenario): Certainty[] {
  const out: Certainty[] = [];
  for (const stage of s.stages) if (!out.includes(stage.certainty)) out.push(stage.certainty);
  return out;
}

export const CERTAINTY_FILTERS: { id: Certainty; label: string }[] = [
  { id: "FACT", label: "Fact" },
  { id: "PREDICTION", label: "Prediction" },
  { id: "THEORY", label: "Theory" },
  { id: "SPECULATION", label: "Speculation" },
  { id: "UNKNOWN", label: "Unknown" },
];

export type WhatIfSort = "featured" | "az" | "category";

export function sortScenarios(list: WhatIfScenario[], sort: WhatIfSort): WhatIfScenario[] {
  const copy = [...list];
  if (sort === "az") return copy.sort((a, b) => a.title.localeCompare(b.title));
  if (sort === "category")
    return copy.sort(
      (a, b) => a.category.localeCompare(b.category) || a.title.localeCompare(b.title),
    );
  return copy.sort(
    (a, b) => Number(b.trending) - Number(a.trending) || a.title.localeCompare(b.title),
  );
}
